/**
 * Mobile Gamepad UI Wrapper (Standalone)
 *
 * ARCHITECTURE DECISION:
 * This is a prop-based wrapper around the MobileGamepadUi component.
 * It ensures proper rendering without dependency on legacy PlaycastContext
 * or player mode checks.
 *
 * WHY THIS EXISTS:
 * - The original MobileGamepadUi is only rendered in Player.tsx when
 *   PlaycastContext.mode.mode === 'player'
 * - This implicit check prevented the mobile gamepad from appearing in
 *   flows that don't set player mode (e.g., Quickstart flow)
 * - This standalone wrapper provides explicit prop-based control
 *
 * KEY DIFFERENCES FROM LEGACY USAGE:
 * - Takes explicit `enabled` prop instead of checking PlaycastContext
 * - Ensures proper z-index and pointer-events handling
 * - No side effects on legacy Player.tsx usage
 *
 * USAGE:
 * ```tsx
 * // Simple usage - always visible
 * <MobileGamepadUiStandalone
 *   gamepadSlot={0}
 *   isMouseEnabled={true}
 *   isKeyboardEnabled={true}
 * />
 *
 * // Controlled usage
 * <MobileGamepadUiStandalone
 *   enabled={isPlaying}
 *   gamepadSlot={playerSlot}
 *   isMouseEnabled={access.mouse}
 *   isKeyboardEnabled={access.keyboard}
 *   isPhysicalGamepadConnected={hasPhysicalGamepad}
 * />
 * ```
 *
 * TECHNICAL NOTES:
 * - The underlying MobileGamepadUi component is unchanged
 * - This is just a thin wrapper that adds explicit control
 * - Z-index is set to ensure proper layering in complex UIs
 *
 * MIGRATION:
 * - New features should use this standalone version
 * - Legacy code can continue using MobileGamepadUi directly
 */

import { MobileGamepadUi } from './MobileGamepadUi';
import type { GamepadLayout } from './store';

export type GamepadLayoutKey = keyof GamepadLayout;

export interface MobileGamepadUiStandaloneProps {
  enabled?: boolean;
  testMode?: boolean;
  gamepadSlot?: number;
  isMouseEnabled?: boolean;
  isKeyboardEnabled?: boolean;
  isPhysicalGamepadConnected?: boolean;
  disconnect?: () => void;
  statsToggle?: () => void;
  // Timer display in Options button (optional)
  timerRemainingSeconds?: number;
  timerTotalSeconds?: number;
  timerPosition?: 'top' | 'bottom';
  /** Switch timer bar to AFK styling (red colors, "AFK" label, pulsing glow) */
  afkMode?: boolean;
  // Theme-based gamepad configuration
  defaultHiddenButtons?: GamepadLayoutKey[];
  invisibleButtons?: GamepadLayoutKey[];
  defaultLayout?: Partial<GamepadLayout>;
  defaultOpacity?: number;
  useCircularButtons?: boolean;
  customButtonIcons?: Partial<Record<GamepadLayoutKey, string>>;
  customButtonSizes?: Partial<Record<GamepadLayoutKey, number>>;
  rightThumbstickBehindButtons?: boolean;
  // Force rotation state - indicates if parent view has been rotated
  isForceRotated?: boolean;
  /** Which face button type is currently highlighted in the attract pattern ('North'|'South'|'East'|'West') */
  attractHighlightedButton?: string;
  /** Glow intensity for the attract highlight (0-100) */
  attractOpacity?: number;
  /** When true, the Options button is visible but not interactive */
  optionsDisabled?: boolean;
  /** When true, the Options/menu bar is completely hidden */
  hideOptions?: boolean;
  /** When true, all gamepad control buttons are hidden but the bottom menu/exit strip remains.
   *  Use when the user has no gamepad access but still needs the exit option. */
  hideGamepadControls?: boolean;
}

/**
 * Standalone mobile gamepad UI with explicit prop-based control.
 *
 * Use this for new features instead of directly using MobileGamepadUi
 * with PlaycastContext checks.
 *
 * NOTE: isMouseEnabled and isKeyboardEnabled are only used by ButtonMenu
 * for settings display, they don't control UI visibility.
 */
export const MobileGamepadUiStandalone = ({
  enabled = true,
  testMode = false,
  gamepadSlot,
  isMouseEnabled,
  isKeyboardEnabled,
  isPhysicalGamepadConnected,
  disconnect,
  statsToggle,
  timerRemainingSeconds,
  timerTotalSeconds,
  timerPosition,
  afkMode,
  defaultHiddenButtons,
  invisibleButtons,
  defaultLayout,
  defaultOpacity,
  useCircularButtons,
  customButtonIcons,
  customButtonSizes,
  rightThumbstickBehindButtons,
  isForceRotated,
  attractHighlightedButton,
  attractOpacity,
  optionsDisabled,
  hideOptions,
  hideGamepadControls,
}: MobileGamepadUiStandaloneProps) => {
  if (!enabled) return null;

  // No wrapper needed - MobileGamepadUi already handles its own positioning and z-index
  // This is just a thin prop-based control layer
  return (
    <MobileGamepadUi
      testMode={testMode}
      gamepadSlot={gamepadSlot}
      isMouseEnabled={isMouseEnabled}
      isKeyboardEnabled={isKeyboardEnabled}
      isPhysicalGamepadConnected={isPhysicalGamepadConnected}
      disconnect={disconnect}
      statsToggle={statsToggle}
      timerRemainingSeconds={timerRemainingSeconds}
      timerTotalSeconds={timerTotalSeconds}
      timerPosition={timerPosition}
      afkMode={afkMode}
      defaultHiddenButtons={defaultHiddenButtons}
      invisibleButtons={invisibleButtons}
      defaultLayout={defaultLayout}
      defaultOpacity={defaultOpacity}
      useCircularButtons={useCircularButtons}
      customButtonIcons={customButtonIcons}
      customButtonSizes={customButtonSizes}
      rightThumbstickBehindButtons={rightThumbstickBehindButtons}
      isForceRotated={isForceRotated}
      attractHighlightedButton={attractHighlightedButton}
      attractOpacity={attractOpacity}
      optionsDisabled={optionsDisabled}
      hideOptions={hideOptions}
      hideGamepadControls={hideGamepadControls}
    />
  );
};

